import type {
  AssetListCheckResponse,
  AssetListEntity,
  AssetListInput,
  AssetListTract,
  AssetNameResult,
  NamedAssetListEntity,
} from './types';

/** Nothing pasted, no file. */
export const EMPTY_ASSET_LIST: AssetListInput = { text: '', file: null };

/**
 * Every character that is not a letter or digit, as the server's
 * `Packages::AssetNameResolver` strips them from both ends of a name: Ruby's
 * `[[:alnum:]]` on a UTF-8 string is the Alphabetic property plus decimal
 * digits, in any script. Quotes of any style, brackets, punctuation, the
 * byte-order mark, zero-width and non-breaking spaces all come off.
 */
const EDGES = /^[^\p{Alphabetic}\p{Nd}]+|[^\p{Alphabetic}\p{Nd}]+$/gu;

/** Commas, semicolons and any whitespace, newlines included. */
const SEPARATORS = /[\s,;]+/u;

const PRINTABLE_ASCII = /^[ -~]*$/;

/**
 * The case-insensitive identity of a name: the key the check results and the
 * header tags are filed under. Lowercased one character at a time, as Ruby's
 * `String#downcase` does, so a capital sigma at the end of a word lowers to
 * the same letter the server compares (`toLowerCase` on the whole string would
 * give the final form).
 *
 * @param {string} name - A name, already stripped
 * @return {string} Its key
 */
export const assetNameKey = (name: string): string =>
  PRINTABLE_ASCII.test(name)
    ? name.toLowerCase()
    : Array.from(name, (character) => character.toLowerCase()).join('');

/**
 * Names once each, case-insensitively, in order; the first spelling wins.
 *
 * @param {Iterable<string>} names - Names, possibly repeated
 * @return {string[]} The distinct names
 */
const uniqueNames = (names: Iterable<string>): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const name of names) {
    const key = assetNameKey(name);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(name);
  }
  return unique;
};

/**
 * Split pasted text into Asset Names: on commas, semicolons and any
 * whitespace; every character that is not a letter or digit comes off both
 * ends of each token, as the server strips them; empty tokens drop; repeats
 * collapse case-insensitively, keeping the first spelling as stripped. Every
 * token is a candidate whatever its shape — the check reports one that is not
 * a name as not found. Separate from `parsePastedList`, which three other
 * features share and which splits differently.
 *
 * @param {string} text - The paste box's content
 * @return {string[]} The names, in order
 */
export const parseAssetNames = (text: string): string[] =>
  uniqueNames(
    text
      .split(SEPARATORS)
      .map((token) => token.replace(EDGES, ''))
      .filter((name) => name !== '')
  );

/**
 * The working list: the pasted names, then the file's remaining names, once
 * each case-insensitively, the first spelling winning.
 *
 * @param {AssetListInput} input - Pasted text and file
 * @return {string[]} The names to check
 */
export const workingAssetNames = (input: AssetListInput): string[] =>
  uniqueNames([
    ...parseAssetNames(input.text),
    ...(input.file?.assetNames ?? []),
  ]);

/**
 * Take one name out of the working list, as dismissing its header tag does:
 * the pasted text is rewritten as its remaining tokens joined by ", " (so line
 * breaks, semicolons and repeated spellings go), and the file, if it listed
 * the name, drops it while staying listed itself.
 *
 * @param {AssetListInput} input - Pasted text and file
 * @param {string} key - The name's {@link assetNameKey}
 * @return {AssetListInput} The input without the name
 */
export const dismissAssetName = (
  input: AssetListInput,
  key: string
): AssetListInput => ({
  text: parseAssetNames(input.text)
    .filter((name) => assetNameKey(name) !== key)
    .join(', '),
  file: input.file && {
    ...input.file,
    assetNames: input.file.assetNames.filter(
      (name) => assetNameKey(name) !== key
    ),
  },
});

/** What the name check has answered so far, kept in `assetNameCheckAtom`. */
export interface AssetNameCheck {
  /** Every answered name, by {@link assetNameKey}. */
  results: ReadonlyMap<string, AssetNameResult>;
  /** Preview rows of the matched Tracts, by Salesforce id. */
  tracts: ReadonlyMap<string, AssetListTract>;
  /**
   * The names, by key, each preview row was worked out from. The server decides
   * whether a Tract is whole or held in part over the names of one request, so a
   * row stands only while the list gives its Tract these same names.
   */
  tractNames: ReadonlyMap<string, ReadonlySet<string>>;
  /** Set when the last check failed, with the server's reason if it gave one. */
  failure: { reason: string | null } | null;
}

export const EMPTY_ASSET_NAME_CHECK: AssetNameCheck = {
  results: new Map(),
  tracts: new Map(),
  tractNames: new Map(),
  failure: null,
};

/**
 * Fold one batch's answer into the check. Every name sent comes out answered:
 * one the server answered under another spelling (possible only where its
 * Unicode tables and the browser's disagree about a character at a name's edge
 * or its case) counts as not found rather than waiting for an answer that
 * never comes. Each Tract the batch's matches touch takes the row the server
 * worked out from them, or loses its row if the server sent none.
 *
 * @param {AssetNameCheck} check - The check so far
 * @param {string[]} sent - The names the batch sent
 * @param {AssetListCheckResponse} response - The server's answer
 * @return {AssetNameCheck} The check with the batch merged
 */
export const mergeAssetNameCheck = (
  check: AssetNameCheck,
  sent: string[],
  response: AssetListCheckResponse
): AssetNameCheck => {
  const results = new Map(check.results);
  for (const result of response.assetNameResults) {
    results.set(assetNameKey(result.name), result);
  }
  const touched = new Map<string, Set<string>>();
  for (const name of sent) {
    const key = assetNameKey(name);
    const result = results.get(key);
    if (result === undefined) {
      results.set(key, {
        name,
        status: 'not_found',
        matchCount: 0,
        tractSalesforceId: null,
        subtractSalesforceId: null,
      });
    } else if (result.tractSalesforceId) {
      const keys = touched.get(result.tractSalesforceId) ?? new Set<string>();
      touched.set(result.tractSalesforceId, keys.add(key));
    }
  }
  const tracts = new Map(check.tracts);
  const tractNames = new Map(check.tractNames);
  for (const tractId of touched.keys()) {
    tracts.delete(tractId);
    tractNames.delete(tractId);
  }
  for (const tract of response.tracts) {
    tracts.set(tract.salesforceId, tract);
    tractNames.set(
      tract.salesforceId,
      touched.get(tract.salesforceId) ?? new Set()
    );
  }
  return { ...check, results, tracts, tractNames };
};

/**
 * The names of the list whose Tract's preview row was worked out from other
 * names than the list now gives that Tract: a Subtract named after its Tract was
 * checked, two Subtracts of one Tract checked apart, or a name taken out. Sent
 * again together, they give the row the server's grouping for the whole list.
 * Grouped by Tract, a group to keep in one request.
 *
 * @param {string[]} names - The working list ({@link workingAssetNames})
 * @param {AssetNameCheck} check - The answers so far
 * @return {string[][]} The names to send again, one group per Tract
 */
export const assetNamesToRegroup = (
  names: string[],
  check: AssetNameCheck
): string[][] => {
  const byTract = new Map<string, string[]>();
  for (const name of names) {
    const tractId = check.results.get(assetNameKey(name))?.tractSalesforceId;
    if (!tractId || !check.tracts.has(tractId)) continue;
    const group = byTract.get(tractId);
    if (group) {
      group.push(name);
    } else {
      byTract.set(tractId, [name]);
    }
  }
  return [...byTract].flatMap(([tractId, group]) => {
    const workedOut = check.tractNames.get(tractId);
    const same =
      workedOut?.size === group.length &&
      group.every((name) => workedOut.has(assetNameKey(name)));
    return same ? [] : [group];
  });
};

/**
 * What the check has to send: each name with no answer yet, on its own, and
 * each group {@link assetNamesToRegroup} gives.
 *
 * @param {string[]} names - The working list ({@link workingAssetNames})
 * @param {AssetNameCheck} check - The answers so far
 * @return {string[][]} The names to send, in groups to keep in one request
 */
export const assetNamesToCheck = (
  names: string[],
  check: AssetNameCheck
): string[][] => [
  ...names
    .filter((name) => !check.results.has(assetNameKey(name)))
    .map((name) => [name]),
  ...assetNamesToRegroup(names, check),
];

/**
 * Packs groups of names into requests of at most `size` names, never splitting
 * a group: the server works a Tract out over the names of one request.
 *
 * @param {string[][]} groups - The names to send ({@link assetNamesToCheck})
 * @param {number} size - The most names one request takes
 * @return {string[][]} The requests' names
 */
export const assetNameBatches = (
  groups: string[][],
  size: number
): string[][] => {
  const batches: string[][] = [];
  for (const group of groups) {
    const last = batches[batches.length - 1];
    if (last && last.length + group.length <= size) last.push(...group);
    else batches.push([...group]);
  }
  return batches;
};

/**
 * Whether an entity has a name to show.
 *
 * @param {AssetListEntity} entity - An owning entity
 * @return {boolean} True unless its name is gone
 */
const isNamed = (entity: AssetListEntity): entity is NamedAssetListEntity =>
  entity.name !== null;

/** A matched name of the working list and the Tract it contributes. */
export interface MatchedAssetName {
  /** As listed. */
  name: string;
  key: string;
  /** Undefined only if the check left the Tract's preview row out. */
  tract: AssetListTract | undefined;
  /** The Subtract the name matched, for a Subtract name; null otherwise. */
  subtractSalesforceId: string | null;
}

/** The asset list, read against the check. Derived, never stored. */
export interface AssetListSummary {
  /** The working list. */
  names: string[];
  /** Names with no answer yet, or whose Tract is still being worked out. */
  unchecked: string[];
  /** Matched names, in list order. */
  matched: MatchedAssetName[];
  /** Not-found and ambiguous names, in list order, spelled as listed. */
  unmatched: AssetNameResult[];
  /** The contributed Tracts, once each, in the order of the first name that contributed each. */
  tracts: AssetListTract[];
  /** The contributed Tracts' Salesforce ids, once each and sorted, a Tract with no preview row included. */
  tractIds: string[];
  /** The assets the Tracts make: 1 per whole Tract, 1 per listed Subtract of one held in part. */
  assetCount: number;
  /**
   * Their owning entities with a name, once each, in the order first met. An
   * entity with no name left (hard-deleted) has nothing to link or offer.
   */
  entities: NamedAssetListEntity[];
}

/**
 * Read the working list against the name check. Takes the list rather than
 * the input so `names` keeps its identity while answers arrive: the check
 * restarts whenever that identity changes. A name whose Tract has to be worked
 * out again ({@link assetNamesToRegroup}) counts as unchecked until it is.
 *
 * @param {string[]} names - The working list ({@link workingAssetNames})
 * @param {AssetNameCheck} check - The answers so far
 * @return {AssetListSummary} The list's results, layer by layer
 */
export const summarizeAssetList = (
  names: string[],
  check: AssetNameCheck
): AssetListSummary => {
  const regrouping = new Set(
    assetNamesToRegroup(names, check).flat().map(assetNameKey)
  );
  const unchecked: string[] = [];
  const matched: MatchedAssetName[] = [];
  const unmatched: AssetNameResult[] = [];
  const tractIds = new Set<string>();
  for (const name of names) {
    const key = assetNameKey(name);
    const result = check.results.get(key);
    if (result === undefined || regrouping.has(key)) {
      unchecked.push(name);
    } else if (result.status === 'matched' && result.tractSalesforceId) {
      tractIds.add(result.tractSalesforceId);
      matched.push({
        name,
        key,
        tract: check.tracts.get(result.tractSalesforceId),
        subtractSalesforceId: result.subtractSalesforceId,
      });
    } else {
      unmatched.push({ ...result, name });
    }
  }

  const tracts = [
    ...new Map(
      matched.flatMap(({ tract }) =>
        tract ? [[tract.salesforceId, tract] as const] : []
      )
    ).values(),
  ];
  const assetCount = tracts.reduce(
    (sum, tract) =>
      sum + (tract.subtractCount === null ? 1 : tract.subtracts.length),
    0
  );
  const entities = new Map<number, NamedAssetListEntity>();
  for (const tract of tracts) {
    for (const entity of tract.entities.filter(isNamed)) {
      if (!entities.has(entity.id)) entities.set(entity.id, entity);
    }
  }

  return {
    names,
    unchecked,
    matched,
    unmatched,
    tracts,
    tractIds: [...tractIds].sort(),
    assetCount,
    entities: [...entities.values()],
  };
};

/**
 * The listed file's name if the file contributed a matched name (a name both
 * pasted and in the file counts for both), else null: the Package Summary
 * names the file only when it brought something in.
 *
 * @param {AssetListInput['file']} file - The listed file, if any
 * @param {MatchedAssetName[]} matched - The matched names
 * @return {string | null} The file's name, or null
 */
export const contributingFileName = (
  file: AssetListInput['file'],
  matched: MatchedAssetName[]
): string | null => {
  if (file === null) return null;
  const keys = new Set(matched.map(({ key }) => key));
  return file.assetNames.some((name) => keys.has(assetNameKey(name)))
    ? file.name
    : null;
};
